import type { ValidationConfig } from '../../../core/dataProcessor/types';

// 🆕 为新 ValidationEngine 导出的配置
export const dynamicTreeMapValidationConfig: ValidationConfig = {
  fields: [
    { field: 'x', type: 'string', required: true },
    { field: 'y', type: 'number', required: true },
    { field: 'group', type: ['string', 'date'], required: true },
    { field: 'color', type: 'number', required: false }
  ],
  businessRules: [
    {
      name: 'dynamic-treemap-sufficient-data',
      validator: data => Array.isArray(data) && data.length >= 1,
      message: 'Dynamic TreeMap requires at least 1 data point',
      suggestion: 'Provide at least 1 data point for TreeMap visualization',
      severity: 'error'
    },
    {
      name: 'dynamic-treemap-multiple-time-points',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        const { encoding } = layer;
        const timePoints = new Set(
          data.map((item: Record<string, unknown>) => item[encoding?.group as string])
        );
        return timePoints.size >= 2;
      },
      message: 'Dynamic TreeMap works best with multiple time points',
      suggestion: 'Provide data from multiple time points for dynamic visualization',
      severity: 'warning'
    },
    {
      name: 'dynamic-treemap-positive-values',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        const { encoding } = layer;
        return data.every((item: Record<string, unknown>) => {
          const value = item[encoding?.y as string];
          return typeof value === 'number' && value > 0;
        });
      },
      message: 'Dynamic TreeMap values should be positive',
      suggestion:
        'TreeMap areas are based on positive values; ensure all y values are greater than 0',
      severity: 'error'
    },
    {
      name: 'dynamic-treemap-category-not-empty',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        const { encoding } = layer;
        return data.every((item: Record<string, unknown>) => {
          const category = item[encoding?.x as string];
          return category && typeof category === 'string' && category.trim().length > 0;
        });
      },
      message: 'Dynamic TreeMap categories cannot be empty',
      suggestion: 'Ensure all category names (x field) are non-empty strings',
      severity: 'error'
    },
    {
      name: 'dynamic-treemap-time-point-data-completeness',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        const { encoding } = layer;
        const groupedData = new Map<string, number>();

        data.forEach((item: Record<string, unknown>) => {
          const timePoint = String(item[encoding?.group as string]);
          groupedData.set(timePoint, (groupedData.get(timePoint) || 0) + 1);
        });

        // 每个时间点至少需要1个数据点
        return Array.from(groupedData.values()).every(count => count >= 1);
      },
      message: 'Each time point must have at least 1 data point',
      suggestion: 'Ensure each time point in the group field has corresponding data',
      severity: 'error'
    },
    {
      name: 'dynamic-treemap-color-range-valid',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        const { encoding } = layer;
        if (!encoding?.color) {
          return true; // Color field is optional
        }

        return data.every((item: Record<string, unknown>) => {
          const color = item[encoding.color as string];
          return color === undefined || (typeof color === 'number' && Number.isFinite(color));
        });
      },
      message: 'Dynamic TreeMap color values must be valid numbers',
      suggestion: 'Ensure all color field values are valid, finite numbers',
      severity: 'warning'
    },
    {
      name: 'dynamic-treemap-optimal-categories',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        const { encoding } = layer;
        const categories = new Set(
          data.map((item: Record<string, unknown>) => item[encoding?.x as string])
        );
        return categories.size >= 3 && categories.size <= 50;
      },
      message: 'Dynamic TreeMap works best with 3-50 categories',
      suggestion: 'Provide 3-50 categories for optimal TreeMap visualization',
      severity: 'warning'
    },
    {
      name: 'dynamic-treemap-time-consistency',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return true;
        }
        const { encoding } = layer;
        const timePoints = [
          ...new Set(data.map((item: Record<string, unknown>) => item[encoding?.group as string]))
        ];

        // 检查每个时间点的类别一致性
        if (timePoints.length <= 1) {
          return true;
        }

        const categoriesByTime = timePoints.map(timePoint => {
          const timeData = data.filter(
            (item: Record<string, unknown>) => item[encoding?.group as string] === timePoint
          );
          return new Set(
            timeData.map((item: Record<string, unknown>) => item[encoding?.x as string])
          );
        });

        // 检查是否有共同的类别
        const firstTimeCategories = categoriesByTime[0];
        return Array.from(firstTimeCategories).some(category =>
          categoriesByTime.every(timeCategories => timeCategories.has(category as string))
        );
      },
      message: 'Dynamic TreeMap time points should have some common categories',
      suggestion: 'Ensure different time points share some common categories for better comparison',
      severity: 'warning'
    }
  ]
};
